import React, {useEffect} from 'react'
import PropTypes from 'prop-types'
import {Box, Grid, Text, useBreakpointValue, useStyleConfig} from '@chakra-ui/react'
import {useProducts} from '@salesforce/commerce-sdk-react'
import {debounce} from 'lodash'
import {PricingTile} from './pricing-tile'
import {remCalc} from '../../../theme/_utils'
import {useGoogleTagManager} from '../../../custom-api/hooks/use-google-tag-manager'
import {item_gtm_event, polish_ecommerce_gtm_event} from '../../../custom-api/layers/default'
import {getPriceDataUseProduct} from '../../../utils/product-utils'
import parse from 'html-react-parser'

export const Pricing = ({title, heading = '', bodycopy = '', pricingGroup = []}) => {
    const styles = useStyleConfig('Pricing')

    const products = pricingGroup
        .map((group) =>
            group.products?.data?.length && group.products?.data?.length > 0
                ? {
                      productId: group.products.data[0].id,
                      isBestSeller: group.is_best_seller,
                      timing: group.timing,
                  }
                : null,
        )
        .filter((product) => !!product)

    const {data: productsData, isLoading} = useProducts(
        {
            parameters: {
                ids: products.map((product) => product.productId),
                expand: ['availability', 'images'],
            },
            headers: {
                'x-debug': 'pricing-index',
            },
        },
        {
            enabled: products?.length > 0,
        },
    )

    const productsNumber = productsData?.data?.length || 0

    const tilesGridClass = useBreakpointValue({
        base: '',
        md: `tablet-tiles-grid-${productsNumber}`,
        lg: `desktop-tiles-grid-${productsNumber}`,
    })

    const repeat = useBreakpointValue({
        base: 1,
        md: Math.min(...[2, productsNumber]),
        lg: Math.min(...[4, productsNumber]),
    })

    const isMobile = useBreakpointValue({base: true, md: false})
    const containerHasPaddingTop = !!heading || !!bodycopy

    const CONTAINERS_SELECTOR = '.pricing-tile'
    const RESIZABLES_SELECTOR = '.pricing-resizable'
    const DEBOUNCE_WAIT = 10

    const debouncedResize = debounce(() => {
        const containers = [...document.querySelectorAll(CONTAINERS_SELECTOR)]

        if (isMobile) {
            // Write only — no forced reflow
            containers.forEach((container) => {
                Array.from(container.querySelectorAll(RESIZABLES_SELECTOR)).forEach((resizable) => {
                    resizable.style.minHeight = null
                })
            })
        } else {
            // Step 1 — Write: reset all minHeight values before reading
            containers.forEach((container) => {
                Array.from(container.querySelectorAll(RESIZABLES_SELECTOR)).forEach((resizable) => {
                    resizable.style.minHeight = null
                })
            })

            // Step 2 — Read: batch all offsetHeight reads in a single pass
            const containersResizablesHeights = containers.map((container) =>
                [...container.querySelectorAll(RESIZABLES_SELECTOR)].map(
                    (resizable) => resizable.offsetHeight,
                ),
            )

            // Step 3 — Write: apply the computed max heights
            const maxHeights = new Array(
                Math.max(...containersResizablesHeights.map((c) => c.length)),
            )
                .fill(null)
                .map((_, i) => Math.max(...containersResizablesHeights.map((h) => h[i])))

            containers.forEach((container) => {
                Array.from(container.querySelectorAll(RESIZABLES_SELECTOR)).forEach(
                    (resizable, i) => {
                        resizable.style.minHeight = `${maxHeights[i]}px`
                    },
                )
            })
        }
    }, DEBOUNCE_WAIT)

    useEffect(() => {
        debouncedResize()
        window.addEventListener('resize', debouncedResize)

        return () => window.removeEventListener('resize', debouncedResize)
    }, [productsData, isMobile])

    const {addLayer} = useGoogleTagManager()
    useEffect(() => {
        if (productsData && !isLoading) {
            addLayer(polish_ecommerce_gtm_event.bind(this))
            addLayer(
                item_gtm_event.bind(this, {
                    event: 'view_item_list',
                    item_list_name: title,
                    items: productsData?.data?.map((product, i) => ({
                        item_name: product?.c_defaultName,
                        item_id: product?.c_variants?.[0]?.productId,
                        item_category: product?.c_defaultCategoryName,
                        item_variant: product?.id || product?.c_subModelIdentifier,
                        price: +(product?.price / (1 + product?.c_taxRate / 100)).toFixed(2),
                        fullprice: getPriceDataUseProduct(product)?.isOnSale
                            ? 'reduced'
                            : 'fullprice',
                        rating_points: '',
                        erp_category: product?.c_materialGroup || product?.c_MaterialGroup,
                        prdha_category: product?.c_prdhaCategory || product?.c_prdha_category,
                        index: i + 1,
                        stock_status:
                            product?.inventory?.stockLevel > 0 ? 'in-stock' : 'out-of-stock',
                    })),
                }),
            )
        }
    }, [productsData, isLoading])

    return (
        <Box
            data-testid="pricing"
            __css={{...styles.container}}
            pt={containerHasPaddingTop ? {base: remCalc(16), md: remCalc(24)} : 0}
        >
            {heading && (
                <Text as="h2" {...styles.heading}>
                    {heading}
                </Text>
            )}
            {bodycopy && <Box {...styles.bodycopy}>{parse(bodycopy)}</Box>}
            {productsData?.data?.length && productsData?.data?.length > 0 && (
                <Grid
                    templateColumns={`repeat(${repeat}, 1fr)`}
                    data-testid="pricing-tiles"
                    {...styles.tiles}
                    className={tilesGridClass}
                >
                    {productsData?.data.map((productData, tIndex) => (
                        <PricingTile
                            key={`pricing-tile-${tIndex}`}
                            productData={productData}
                            {...products.find((product) => product.productId === productData.id)}
                        />
                    ))}
                </Grid>
            )}
        </Box>
    )
}

Pricing.propTypes = {
    title: PropTypes.string,
    heading: PropTypes.string,
    bodycopy: PropTypes.string,
    pricingGroup: PropTypes.arrayOf(PropTypes.shape()),
}
